import { RootState } from "app/store";
import { EControlType } from "components/ControlWrapper";
import { EValidationSeverity, IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { friendlyValue } from "global/Application";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { cyrb53, formatDate, parseDate } from "global/Utility/TSFunctions";
import {
    EBenefitStatus as FAM_EBenefitStatus,
    IDateValue as FAM_IDateValue,
    IEntityReferenceValue as FAM_IEntityReferenceValue
} from "model/FlexibleAttributeModel";
import {
    bcbs_config_attribute as IXrmTypes_bcbs_config_attribute,
    contact as IXrmTypes_contact
} from 'model/CRM';
import { EColumnExtractDataTypes } from "./EColumnExtractDataTypes";
import { IColumnExtract } from "./IColumnExtract.type";
import { IDatasetExtract } from "./IDatasetExtract";
import { IRowExtract } from "./IRowExtract.type";
import { ExcelStyles } from "./ExcelStyles";
import { ChangeTypePermissions } from "model/ChangeTypePermissions";
import { FlexibleFieldGenerator } from "model/FlexibleFieldGenerator";
import { EvaluateFieldDisability } from "views/BlueRelationship/FlexibleFieldView";
import { $att_$group_number_$bcbs_contactid_id, $att_$group_number_$bcbs_groupleaderid_id } from "model/ConfigAttributeConstants";

// Template IDs for different export modes
// https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=818b0bc2-a215-ed11-b83c-00224820a835&pagetype=entityrecord&etn=bcbs_config_variable&id=d01e15e1-1c41-f011-b4cb-6045bdef1703
// https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/633631
export const GROUP_STRUCTURE_TEMPLATE_ID = 'd01e15e1-1c41-f011-b4cb-6045bdef1703';

// Config attribute IDs for Group Number lookup fields (flexible fields)
// These match the backend config_attribute records
export const bcbs_config_attributeid_GroupNumber_Contact = $att_$group_number_$bcbs_contactid_id;
export const bcbs_config_attributeid_GroupNumber_GroupLeader = $att_$group_number_$bcbs_groupleaderid_id;

// Cancellation section ID for applying red header color
export const bcbs_config_sectionid_Cancellation = 'e2aae925-d18d-ec11-b400-0022480a93ed';

// Field identifiers for Group Number direct properties (NOT config attributes)
// These are used to match columns to dropdown tables in the export
export const GROUPNUMBER_FIELD_CES_CLIENT_NUMBER = 'cesClientNumber';
export const GROUPNUMBER_FIELD_PLAN = 'plan';
export const GROUPNUMBER_FIELD_ACCOUNTS_RECEIVABLE = 'accountsReceivable';
export const GROUPNUMBER_FIELD_STOP_LOSS = 'stopLoss';
export const GROUPNUMBER_FIELD_STATUS = 'status';

interface IRowApi {
    [key: string]: string;
    '$type': "System.Collections.Generic.Dictionary`2[[System.String, System.Private.CoreLib],[System.String, System.Private.CoreLib]], System.Private.CoreLib";
}

export interface IGroupStructureExportScope {
    state: RootState;
    dataSource: AppDataSource;
    contactsByAccountId: IXrmTypes_contact[] | null;
    /**
     * When true, generates a full Report export: every column is included regardless of Change Type,
     * no columns are grayed out/locked (Reports are never imported, so Change-Type restrictions do not
     * apply), and the file is flagged in its Metadata sheet so it cannot be used for import.
     */
    isReport?: boolean;
}

export interface IGroupStructureExportResult {
    templateBlob: Blob;
    messages: { message: string }[];
    logs: IValidationMessage[];
}

/**
 * Legacy Group Structure Excel Export
 * Uses the template-based approach with getTemplate API for backward compatibility with import.
 */
export async function LegacyGroupStructureExcelExtract(scope: IGroupStructureExportScope): Promise<IGroupStructureExportResult> {
    const { state, dataSource, contactsByAccountId } = scope;
    const logs: IValidationMessage[] = [];

    const fileB64 = await dataSource.crm.downloadFileAttributeBase64(
        'bcbs_config_variables',
        GROUP_STRUCTURE_TEMPLATE_ID,
        'bcbs_attachment'
    );

    const requestJson = {
        "$type": "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal",
        "FileBase64": fileB64,
        "ReadOnly": false,
        "Requests": []
    } as any;

    // Plans
    // -------------------------------------------------------------------------------
    const planList = state.data.json?.plans.filter(q => !q.deactivated) ?? [];

    if (planList.length > 0) {
        const rows = planList.map(q => ({
            "Plans": q.name
        }));

        requestJson.Requests.push({
            "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
            "TableName": "Table_Plan",
            "SheetName": "Plans",
            "IsFirstRowHeader": true,
            "Rows": rows,
            "comment": null
        });
    } else {
        logs.push({
            date: new Date(),
            title: `Configuration is missing Plans.`,
            description: ``,
            type: EValidationSeverity.warn
        });
    }

    // CES Client Numbers
    // -------------------------------------------------------------------------------
    const cesList = state.data.json?.groupStructure?.cesNumbers.filter(q => !q.deactivated) ?? [];

    if (cesList.length > 0) {
        requestJson.Requests.push({
            "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
            "TableName": "Table_CESClientNumbers",
            "SheetName": "CES Client Numbers",
            "IsFirstRowHeader": true,
            "Rows": cesList.map(q => ({
                "ID": q.id,
                "CES Client Number": q.cesNumber,
                "Status": q.deactivated ? "Deactivated" : "Active"
            })),
            "comment": null
        });
    } else {
        logs.push({
            date: new Date(),
            title: `Configuration is missing CES Client Numbers.`,
            description: ``,
            type: EValidationSeverity.warn
        });
    }

    // Departments
    // -------------------------------------------------------------------------------
    const departmentList = state.data.json?.groupStructure?.departments.filter(q => !q.deactivated) ?? [];

    if (departmentList.length > 0) {
        requestJson.Requests.push({
            "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
            "TableName": "Table_Departments",
            "SheetName": "Departments",
            "IsFirstRowHeader": true,
            "Instructions": {
                "deleteRowsByIndex": "2"
            },
            "Rows": departmentList.map(q => ({
                "ID": q.id,
                "CES Client Number": q.cesNumberRef?.name,
                "Department Number": q.name,
                "Description": q.description,
                "Status": q.deactivated ? "Deactivated" : "Active"
            })),
            "comment": null
        });
    } else {
        logs.push({
            date: new Date(),
            title: `Configuration does not have any Departments.`,
            description: ``,
            type: EValidationSeverity.info
        });
    }

    // Accounts Receivable
    // -------------------------------------------------------------------------------
    const accountsReceivableList = state.data.json?.claimsARs.filter(q => !q.deactivated) ?? [];

    if (accountsReceivableList.length > 0) {
        const rows = accountsReceivableList.map(q => ({
            "Accounts Receivable": q.name
        }));

        requestJson.Requests.push({
            "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
            "TableName": "Table_AccountsReceivable",
            "SheetName": "Accounts Receivable",
            "IsFirstRowHeader": true,
            "Rows": rows,
            "comment": null
        });
    } else {
        logs.push({
            date: new Date(),
            title: `Configuration is missing Accounts Receivable.`,
            description: ``,
            type: EValidationSeverity.warn
        });
    }

    // Stop Loss
    // -------------------------------------------------------------------------------
    const stopLossList = state.data.json?.stopLoss.filter(q => !q.deactivated) ?? [];

    if (stopLossList.length > 0) {
        const rows = stopLossList.map(q => ({
            "Stop Loss": q.name
        }));

        requestJson.Requests.push({
            "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
            "TableName": "Table_Stop_Loss",
            "SheetName": "Stop Loss",
            "IsFirstRowHeader": true,
            "Rows": rows,
            "comment": null
        });
    } else {
        logs.push({
            date: new Date(),
            title: `Configuration is missing Stop Loss.`,
            description: ``,
            type: EValidationSeverity.warn
        });
    }

    const usedRefs: {
        contacts: (FAM_IEntityReferenceValue | null)[]
    } = {
        contacts: [null as any]
    };

    // Group Numbers
    // -------------------------------------------------------------------------------
    if (state.data.json?.groupStructure?.groupNumbers?.length > 0) {
        const updateSubgroupsRequest = {
            "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
            "TableName": "Table_Subgroups",
            "SheetName": "Import Template",
            "IsFirstRowHeader": true,
            "Rows": [] as IRowApi[],
            "Instructions": {
                "deleteRowsByIndex": "2"
            },
            "comment": null
        };

        state.data.json.groupStructure.groupNumbers.forEach((eachGN) => {
            const row: IRowApi = {
                '$type': "System.Collections.Generic.Dictionary`2[[System.String, System.Private.CoreLib],[System.String, System.Private.CoreLib]], System.Private.CoreLib",
            };

            row['ID'] = eachGN.id;
            row['Group Number'] = eachGN.groupNumber ?? ``;
            row['Group Number'] = `<formula>:_xlfn.CONCAT(Table_Subgroups[[#This Row],[Master Prefix]],\"-\",Table_Subgroups[[#This Row],[Master Base]],\"-\",Table_Subgroups[[#This Row],[Suffix]])`;
            row['CES Client Number'] = eachGN.cesNumberRef?.name ?? ``;

            // 12/05/2022
            // After conversation with Sebrina T, it was decided to not export deactivated group numbers to avoid user confusion
            if (eachGN.deactivated) {
                logs.push({
                    date: new Date(),
                    title: `Group Number (${eachGN.groupNumber}) is deactivated and has been skipped.`,
                    description: ``,
                    type: EValidationSeverity.info
                });
                return;
            }

            if (!eachGN.groupNumber) {
                logs.push({
                    date: new Date(),
                    title: `Group Number (${eachGN.id}) is missing value for Group Number.`,
                    description: ``,
                    type: EValidationSeverity.warn
                });
            }

            if (!eachGN.cesNumberRef?.name) {
                logs.push({
                    date: new Date(),
                    title: `Group Number (${eachGN.groupNumber}) is missing CES Client Number.`,
                    description: ``,
                    type: EValidationSeverity.warn
                });
            }

            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/251035
            if (eachGN.deactivated && eachGN.status?.value === FAM_EBenefitStatus["In-Progress"]) {
                logs.push({
                    date: new Date(),
                    title: `Group Number (${eachGN.groupNumber}) skip due to conflict on status and deactivated.`,
                    description: ``,
                    type: EValidationSeverity.warn
                });
                return;
            }

            if (eachGN.prefix) row['Master Prefix'] = eachGN.prefix;
            if (eachGN.base) row['Master Base'] = eachGN.base;
            if (eachGN.suffix) row['Suffix'] = eachGN.suffix;

            if (eachGN.prefix && eachGN.base) {
                row['Prefix-Base'] = `${eachGN.prefix}-${eachGN.base}`;
                row['Prefix-Base'] = `<formula>:_xlfn.CONCAT(Table_Subgroups[[#This Row],[Master Prefix]],\"-\",Table_Subgroups[[#This Row],[Master Base]])`;
            }

            if (eachGN.planRef?.name) {
                row['Plan'] = eachGN.planRef.name;
            }

            if (eachGN.accountsReceivableRef?.name) {
                row['Accounts Receivable'] = eachGN.accountsReceivableRef.name;
            }

            if (eachGN.stopLossRef?.name) {
                row['Stop Loss'] = eachGN.stopLossRef.name;
            }

            if (eachGN.deactivated) {
                row['Status'] = "Deactivated";
            } else {
                if (eachGN.status?.value === FAM_EBenefitStatus.Cancelled)
                    row['Status'] = "Cancelled";
                else
                    row['Status'] = "Active";
            }

            eachGN.fields.forEach(eachField => {
                try {
                    const attribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                    const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];

                    const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
                    const ffValue = eachField.values[0].value;

                    if (ffValue) {
                        const name = attribute.bcbs_name!;
                        let value = '';

                        if (controlType === EControlType.lookup) {
                            const ref = (ffValue as FAM_IEntityReferenceValue);

                            if (ref.type === "entity" && ref.entityType === "contact") {
                                let ix = usedRefs.contacts.findIndex(q => q && q.id === ref.id);

                                if (ix < 0)
                                    usedRefs.contacts.push(ref);

                                ix = usedRefs.contacts.findIndex(q => q && q.id === ref.id);
                                value = `${ref.name} - (${ix})`;
                            }
                        } else if (controlType === EControlType.date) {
                            const ff = (ffValue as FAM_IDateValue);
                            value = formatDate(parseDate(ff.value, 'local:yyyy-MM-dd'), 'local:MM/dd/yyyy');
                        } else {
                            value = friendlyValue(controlType, ffValue);
                        }

                        row[name] = value;
                    }
                } catch (error) {
                    logs.push({
                        date: new Date(),
                        title: `Group Number(${eachGN.groupNumber}), converting ${eachField.name}/${eachField.configRef.name} to flexible field value failed.`,
                        description: ``,
                        type: EValidationSeverity.warn
                    });
                }
            });

            updateSubgroupsRequest.Rows.push(row);
        });

        if (updateSubgroupsRequest.Rows.length > 0)
            requestJson.Requests.push(updateSubgroupsRequest);
    }

    // Contacts
    // -------------------------------------------------------------------------------
    if (contactsByAccountId && contactsByAccountId.length > 0) {
        const rows: { Contacts: string; Data: string; }[] = [];

        const contactToEntityRef = (contact: IXrmTypes_contact): FAM_IEntityReferenceValue => ({
            entityType: 'contact',
            id: contact.contactid!,
            name: contact.fullname!,
            type: 'entity'
        });

        // Add all contacts that have not been used
        contactsByAccountId.forEach(eachContact => {
            const ref = contactToEntityRef(eachContact);
            const ix = usedRefs.contacts.findIndex(q => q && q.id === ref.id);

            if (ix < 0)
                usedRefs.contacts.push(ref);
        });

        usedRefs.contacts.forEach((entityRef, ix) => {
            // First entry starts with null
            if (!entityRef) return;

            rows.push({
                "Contacts": `${entityRef.name} - (${ix})`,
                "Data": btoa((encodeURIComponent(JSON.stringify(entityRef)))),
            });
        });

        requestJson.Requests.push({
            "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
            "TableName": "Table_Contacts",
            "SheetName": "Contacts",
            "IsFirstRowHeader": true,
            "Rows": rows,
            "comment": null
        });
    } else {
        logs.push({
            date: new Date(),
            title: `Configuration's account has not been associated with any contact.`,
            description: ``,
            type: EValidationSeverity.warn
        });
    }

    // Meta Data
    // -------------------------------------------------------------------------------
    const metadata: { Key: string; Value?: string; Description?: string }[] = [
        {
            Key: `bcbs_name`,
            Value: state.data.snapshot.bcbs_name,
            Description: ''
        },
        {
            Key: `bcbs_blobversion`,
            Value: state.data.snapshot.bcbs_blobversion,
            Description: ''
        },
        {
            Key: `checksum`,
            Value: cyrb53(JSON.stringify(state.data.json.groupStructure)).toLocaleString(),
            Description: ''
        }
    ];

    requestJson.Requests.push({
        "$type": "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
        "TableName": "Table_Metadata",
        "SheetName": "Metadata",
        "IsFirstRowHeader": true,
        "Rows": metadata,
        "comment": null
    });
    debugger;
    const { templateBlob, messages } = await dataSource.getTemplate(requestJson);

    logs.push({
        date: new Date(),
        title: `Export complete.`,
        type: EValidationSeverity.info,
    });

    return { templateBlob, messages, logs };
}

/**
 * Modern Group Structure Excel Export
 * Uses the GenerateEditableExcelExtract API with dynamic dropdown validations.
 * Follows the same pattern as EditFeesExcelExtract - single Import Template sheet with hidden OptionSets sheet.
 */
export default async function GroupStructureExcelExtract(scope: IGroupStructureExportScope): Promise<Blob> {
    const { state, dataSource, contactsByAccountId, isReport } = scope;
    
    const rows: IRowExtract[] = [];
    const columns: Record<string, IColumnExtract> = {};
    let columnIX = 0;

    // Derive current change type permissions from state
    const isFullyInsured = state.data.json.fundingType?.value === 808900002;
    const changeTypeValue = state.data.json.changeType?.value;
    const ctPermissions = (changeTypeValue && ChangeTypePermissions[changeTypeValue])
        ? ChangeTypePermissions[changeTypeValue]
        : ChangeTypePermissions[0];
    const canLinkAR = ctPermissions.groupStructure.groupNumber.link_accountsreceivable;
    const canLinkStopLoss = ctPermissions.groupStructure.groupNumber.link_stoploss;
    // Fully Insured groups don't have AR or Stop Loss - no restriction needed
    // Reports are never imported, so the client does not want any columns grayed out for them -
    // only standard Exports should show the Change-Type locked/disabled styling.
    const arDisabledStyle = (!isReport && !isFullyInsured && !canLinkAR) ? 'disabled' : undefined;
    const slDisabledStyle = (!isReport && !isFullyInsured && !canLinkStopLoss) ? 'disabled' : undefined;

    // Track used contacts for dropdown options (matching legacy format "Name - (index)")
    const usedRefs: {
        contacts: (FAM_IEntityReferenceValue | null)[]
    } = {
        contacts: [null as any]
    };

    // Track dynamic dropdown options for flexible fields (optionsets, lookups)
    const dynamicDropdownOptions: Record<string, string[]> = {};

    // Track which flexible field columns we've discovered
    const discoveredFlexibleFields: Record<string, IXrmTypes_bcbs_config_attribute> = {};

    // Track which flexible field columns are locked (not applicable to current Change Type).
    // Standard Exports gray out columns not applicable to the Change Type; Report exports never
    // apply this locking/graying since Reports are read-only and are never imported.
    const lockedFlexibleFields: Record<string, boolean> = {};

    // ===============================================================================
    // PREPARE DROPDOWN OPTIONS FOR FIXED FIELDS
    // ===============================================================================
    const cesList = state.data.json?.groupStructure?.cesNumbers.filter(q => !q.deactivated) ?? [];

    const cesClientNumberOptions = ['', ...cesList.map(q => q.cesNumber)];

    // Build Plan options
    const planOptions = ['', ...(state.data.json?.plans ?? [])
        .filter(q => !q.deactivated)
        .map(q => q.name)];

    // Build Accounts Receivable options
    // '(clear)' allows users to clear an existing link directly from the dropdown on import
    const accountsReceivableOptions = ['', '(clear)', ...(state.data.json?.claimsARs ?? [])
        .filter(q => !q.deactivated)
        .map(q => q.name)];

    // Build Stop Loss options
    // '(clear)' allows users to clear an existing link directly from the dropdown on import
    const stopLossOptions = ['', '(clear)', ...(state.data.json?.stopLoss ?? [])
        .filter(q => !q.deactivated)
        .map(q => q.name)];

    const statusOptions = ['Active', 'Cancelled', 'Deactivated'];

    // ===============================================================================
    // FIRST PASS: Discover all possible flexible field columns for Group Number.
    // Use the FlexibleFieldGenerator (same source used to render the Group Number
    // flexible field form) so every applicable column is included in the export
    // regardless of Change Type or whether any existing group number has a value
    // for it. Columns not applicable to the current Change Type are still
    // included, but marked locked so they render grayed out (header + cells) and
    // are ignored on import.
    // ===============================================================================
    const flexibleFieldGenerator = new FlexibleFieldGenerator(state.data.config);
    flexibleFieldGenerator.generateGroupNumberFields().forEach(eachField => {
        const attribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
        // An attribute having a configured bcbs_expirationdate does not mean it is currently expired -
        // only exclude it once that expiration date has actually passed. Previously this checked
        // `=== undefined`, which excluded every attribute that merely had an expiration date set,
        // even ones still active, causing columns to be missing from the export.
        const isExpired = !!attribute?.bcbs_expirationdate && new Date(attribute.bcbs_expirationdate).getTime() < Date.now();
        if (attribute && attribute.bcbs_config_attributeid && !isExpired && !discoveredFlexibleFields[attribute.bcbs_config_attributeid]) {
            // Reports are never imported, so the client does not want any columns grayed out for them -
            // only standard Exports should show the Change-Type locked/disabled styling.
            const isLocked = !isReport && EvaluateFieldDisability(changeTypeValue ?? 0, attribute);
            lockedFlexibleFields[attribute.bcbs_config_attributeid] = isLocked;

            discoveredFlexibleFields[attribute.bcbs_config_attributeid] = attribute;

            const masterAttribute = attribute._bcbs_config_masterattributeid_value 
                ? state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value]
                : undefined;

            if (!masterAttribute) return;

            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;

            //they want the order of the columns (after the hard-coded columns) to be based off what the UI looks like in portal
            // make sure we only get attributes that are active and not expired

            //cancelation section needs to be at end and after status. cancelation section ID: e2aae925-d18d-ec11-b400-0022480a93ed
            //need to update backend excel to allow colors of columns. They want all cancelation section id attributes to have a red column header. 
            // if (attribute._bcbs_config_sectionid_value === 'e2aae925-d18d-ec11-b400-0022480a93ed') {
            //     // Apply red column header color

            // }

            //status should be hardcoded, but at end before cancellation columns

            // Build dropdown options for optionset fields
            if (controlType === EControlType.optionset) {
                const optionsetId = attribute._bcbs_config_optionsetid_value;
                if (optionsetId) {
                    const optionsetItems = state.data.config.cache.map_bcbs_config_optionsetitems_bySetId[optionsetId] ?? [];
                    const options = ['', ...optionsetItems
                        .filter(item => item.statecode === 0) // Active items only
                        .map(item => item.bcbs_name ?? '')
                        .filter(name => name !== '')];
                    dynamicDropdownOptions[attribute.bcbs_config_attributeid] = options;
                }
            } 
            // Build dropdown options for boolean fields
            else if (controlType === EControlType.boolean) {
                dynamicDropdownOptions[attribute.bcbs_config_attributeid] = ['', 'Yes', 'No'];
            }
            // Contact lookups will use the contact list built later

            //This sets the Order using Tab as the 1000's place value (hard coded since only two tabs)
            const attrSection = attribute._bcbs_config_sectionid_value 
                ? state.data.config.cache.map_bcbs_config_section[attribute._bcbs_config_sectionid_value]
                : undefined;

            if (!attrSection) return;

            let tabInt = 1000;
            if (attrSection.bcbs_name === "CANCELLATION INFORMATION") {
                tabInt = 9000;
            }

            let dataType = EColumnExtractDataTypes.Default;

            if (controlType === EControlType.optionset || controlType === EControlType.boolean) {
                dataType = EColumnExtractDataTypes.DropDown;
            } else if (controlType === EControlType.lookup) {
                // Contact and Group Leader lookups are dropdowns
                if (attribute.bcbs_config_attributeid === bcbs_config_attributeid_GroupNumber_Contact ||
                    attribute.bcbs_config_attributeid === bcbs_config_attributeid_GroupNumber_GroupLeader) {
                    dataType = EColumnExtractDataTypes.DropDown;
                }
            } else if (controlType === EControlType.currency) {
                dataType = EColumnExtractDataTypes.Currency;
            }

            columns[attribute.bcbs_config_attributeid] = {
                attributeId: attribute.bcbs_config_attributeid,
                order: tabInt + (attrSection.bcbs_sortorder! * 100) + (attribute.bcbs_sortorder ?? 0),//This is where it builds the order using tab + section + attribute
                width: (attribute.bcbs_viewcolumnwidth ?? 150) / 10,
                index: ++columnIX,
                fieldName: attribute.bcbs_config_attributeid,
                hidden: false,
                name: attribute.bcbs_name!,
                dataType: dataType,
                locked: isLocked,
            };
        }
    });

    // ===============================================================================
    // DEFINE FIXED COLUMNS - matching Import Template column order
    // Order: ID, CES Client Number, Master Prefix, Master Base, Suffix, Prefix-Base, 
    //        Group Number, [flexible fields], Status
    // ===============================================================================
    columns['id'] = {
        attributeId: '',
        order: 0,
        width: 30,
        index: ++columnIX,
        fieldName: 'id',
        hidden: true,
        name: 'ID',
        dataType: EColumnExtractDataTypes.Default,
    };

    columns['cesClientNumber'] = {
        attributeId: GROUPNUMBER_FIELD_CES_CLIENT_NUMBER,
        order: 1,
        width: 20,
        index: ++columnIX,
        fieldName: 'cesClientNumber',
        hidden: false,
        name: 'CES Client Number',
        dataType: EColumnExtractDataTypes.DropDown,
    };

    columns['masterPrefix'] = {
        attributeId: '',
        order: 2,
        width: 15,
        index: ++columnIX,
        fieldName: 'masterPrefix',
        hidden: false,
        name: 'Master Prefix',
        dataType: EColumnExtractDataTypes.Default,
    };

    columns['masterBase'] = {
        attributeId: '',
        order: 3,
        width: 15,
        index: ++columnIX,
        fieldName: 'masterBase',
        hidden: false,
        name: 'Master Base',
        dataType: EColumnExtractDataTypes.Default,
    };

    columns['suffix'] = {
        attributeId: '',
        order: 4,
        width: 10,
        index: ++columnIX,
        fieldName: 'suffix',
        hidden: false,
        name: 'Suffix',
        dataType: EColumnExtractDataTypes.Text,  // Text format preserves leading zeros like '01'
    };

    columns['prefixBase'] = {
        attributeId: '',
        order: 5,
        width: 20,
        index: ++columnIX,
        fieldName: 'prefixBase',
        hidden: true,
        name: 'Prefix-Base',
        dataType: EColumnExtractDataTypes.Default,
    };

    columns['groupNumber'] = {
        attributeId: '',
        order: 6,
        width: 20,
        index: ++columnIX,
        fieldName: 'groupNumber',
        hidden: true,
        name: 'Group Number',
        dataType: EColumnExtractDataTypes.Default,
        format: {
            // Excel formula to concatenate Master Prefix (C), Master Base (D), and Suffix (E)
            // Using & operator for maximum Excel version compatibility
            // {row} placeholder will be replaced with actual row number by backend
            formula: 'C{row}&"-"&D{row}&"-"&E{row}'
        }
    };

    columns['plan'] = {
        attributeId: GROUPNUMBER_FIELD_PLAN,
        order: 7,
        width: 20,
        index: ++columnIX,
        fieldName: 'plan',
        hidden: false,
        name: 'Plan',
        dataType: EColumnExtractDataTypes.DropDown,
    };

    columns['accountsReceivable'] = {
        attributeId: GROUPNUMBER_FIELD_ACCOUNTS_RECEIVABLE,
        order: 8,
        width: 20,
        index: ++columnIX,
        fieldName: 'accountsReceivable',
        hidden: false,
        name: 'Accounts Receivable',
        dataType: EColumnExtractDataTypes.DropDown,
        format: arDisabledStyle ? { headerStyleId: arDisabledStyle } : undefined,
        locked: !!arDisabledStyle,
    };

    columns['stopLoss'] = {
        attributeId: GROUPNUMBER_FIELD_STOP_LOSS,
        order: 9,
        width: 20,
        index: ++columnIX,
        fieldName: 'stopLoss',
        hidden: false,
        name: 'Stop Loss',
        dataType: EColumnExtractDataTypes.DropDown,
        format: slDisabledStyle ? { headerStyleId: slDisabledStyle } : undefined,
        locked: !!slDisabledStyle,
    };

    // ===============================================================================
    // ADD FLEXIBLE FIELD COLUMNS (discovered from group numbers)
    // These come after the fixed fields but before Status
    // ===============================================================================
    Object.entries(discoveredFlexibleFields).forEach(([attributeId, attribute]) => {
        const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
        const controlType = masterAttribute?.bcbs_attributetypecode as EControlType;
        
        let dataType = EColumnExtractDataTypes.Default;
        
        if (controlType === EControlType.optionset || controlType === EControlType.boolean) {
            dataType = EColumnExtractDataTypes.DropDown;
        } else if (controlType === EControlType.lookup) {
            // Contact and Group Leader lookups are dropdowns
            if (attributeId === bcbs_config_attributeid_GroupNumber_Contact ||
                attributeId === bcbs_config_attributeid_GroupNumber_GroupLeader) {
                dataType = EColumnExtractDataTypes.DropDown;
            }
        } else if (controlType === EControlType.currency) {
            dataType = EColumnExtractDataTypes.Currency;
        }

        //This sets the Order using Tab as the 1000's place value (hard coded since only two tabs)
        const attrSection = state.data.config.cache.map_bcbs_config_section[attribute._bcbs_config_sectionid_value!];
        let tabInt = 1000;
        if (attrSection.bcbs_name === "CANCELLATION INFORMATION") {
            tabInt = 9000;
        }

        // Apply red header color to cancellation section columns
        const columnFormat = attribute._bcbs_config_sectionid_value === bcbs_config_sectionid_Cancellation
            ? { headerStyleId: 'red' } // Reference to red style
            : undefined;

        columns[attributeId] = {
            attributeId: attributeId,
            order: tabInt + (attrSection.bcbs_sortorder! * 100) + (attribute.bcbs_sortorder ?? 0),//This is where it builds the order using tab + section + attribute
            width: (attribute.bcbs_viewcolumnwidth ?? 150) / 10,
            index: ++columnIX,
            fieldName: attributeId,
            hidden: false,
            name: attribute.bcbs_name!,
            dataType: dataType,
            format: columnFormat,
            locked: lockedFlexibleFields[attributeId] ?? false,
        };
    });

    // Status column comes last (per template structure)
    columns['status'] = {
        attributeId: GROUPNUMBER_FIELD_STATUS,
        order: 8999, // Ensure it's last
        width: 12,
        index: ++columnIX,
        fieldName: 'status',
        hidden: false,
        name: 'Status',
        dataType: EColumnExtractDataTypes.DropDown,
        format: { headerStyleId: 'green' } // Green header for status column
    };

    // ===============================================================================
    // BUILD ROWS - iterate through group numbers
    // ===============================================================================
    state.data.json.groupStructure.groupNumbers
        .filter(q => !q.deactivated)
        .forEach(eachGN => {
            const row: Record<string, string> = {};

            // Fixed fields
            row.id = eachGN.id;
            row.cesClientNumber = eachGN.cesNumberRef?.name ?? '';
            row.masterPrefix = eachGN.prefix ?? '';
            row.masterBase = eachGN.base ?? '';
            row.suffix = eachGN.suffix ?? '';
            row.prefixBase = (eachGN.prefix && eachGN.base) ? `${eachGN.prefix}-${eachGN.base}` : '';
            row.groupNumber = eachGN.groupNumber ?? '';
            row.plan = eachGN.planRef?.name ?? '';
            row.accountsReceivable = eachGN.accountsReceivableRef?.name ?? '';
            row.stopLoss = eachGN.stopLossRef?.name ?? '';

            // Status
            if (eachGN.deactivated) {
                row.status = 'Deactivated';
            } else if (eachGN.status?.value === FAM_EBenefitStatus.Cancelled) {
                row.status = 'Cancelled';
            } else {
                row.status = 'Active';
            }

            // Process flexible fields
            eachGN.fields.forEach(eachField => {
                try {
                    const attribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                    if (!attribute || !attribute.bcbs_config_attributeid) return;
                    
                    const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
                    const controlType = masterAttribute?.bcbs_attributetypecode as EControlType;
                    const ffValue = eachField.values[0]?.value;

                    if (ffValue) {
                        const fieldName = attribute.bcbs_config_attributeid;

                        // Handle contact lookups - matching legacy format "Name - (index)"
                        if (controlType === EControlType.lookup) {
                            const ref = ffValue as FAM_IEntityReferenceValue;
                            if (ref.type === "entity" && ref.entityType === "contact") {
                                let ix = usedRefs.contacts.findIndex(q => q && q.id === ref.id);
                                if (ix < 0) {
                                    usedRefs.contacts.push(ref);
                                }
                                ix = usedRefs.contacts.findIndex(q => q && q.id === ref.id);
                                row[fieldName] = `${ref.name} - (${ix})`;
                            }
                        } else if (controlType === EControlType.date) {
                            const ff = ffValue as FAM_IDateValue;
                            row[fieldName] = formatDate(parseDate(ff.value, 'local:yyyy-MM-dd'), 'local:MM/dd/yyyy');
                        } else {
                            row[fieldName] = friendlyValue(controlType, ffValue);
                        }
                    }
                } catch (error) {
                    console.warn(`Error processing field ${eachField.name}`, error);
                }
            });

            rows.push(row);
        });

    // ===============================================================================
    // BUILD CONTACT OPTIONS for dropdown (after processing all rows)
    // ===============================================================================
    const contactToEntityRef = (contact: IXrmTypes_contact): FAM_IEntityReferenceValue => ({
        entityType: 'contact',
        id: contact.contactid!,
        name: contact.fullname!,
        type: 'entity'
    });

    // Add all contacts from account that haven't been used yet
    (contactsByAccountId ?? []).forEach(eachContact => {
        const ref = contactToEntityRef(eachContact);
        const ix = usedRefs.contacts.findIndex(q => q && q.id === ref.id);
        if (ix < 0) {
            usedRefs.contacts.push(ref);
        }
    });

    // Build contact options (matching legacy format)
    const contactOptions: string[] = [''];
    usedRefs.contacts.forEach((entityRef, ix) => {
        if (entityRef) {
            contactOptions.push(`${entityRef.name} - (${ix})`);
        }
    });

    // ===============================================================================
    // BUILD DATASET
    // ===============================================================================
    const columnsArray = Object.entries(columns)
        .map(kv => kv[1])
        .sort((w, q) => w.order - q.order); // Sort by order, not index

    // Note: Report exports never apply the Change Type based 'locked'/gray-out styling - all
    // columns render normally since Reports are read-only and are never imported.

    // Build dropdown tables for fixed fields
    const dropDownTables: IDatasetExtract['dropDownTables'] = [
        {
            Id: GROUPNUMBER_FIELD_CES_CLIENT_NUMBER,
            Title: 'CES Client Number',
            dropdownOptions: [{ options: cesClientNumberOptions, Title: 'CES Client Number' }]
        },
        {
            Id: GROUPNUMBER_FIELD_PLAN,
            Title: 'Plan',
            dropdownOptions: [{ options: planOptions, Title: 'Plan' }]
        },
        {
            Id: GROUPNUMBER_FIELD_ACCOUNTS_RECEIVABLE,
            Title: 'Accounts Receivable',
            dropdownOptions: [{ options: accountsReceivableOptions, Title: 'Accounts Receivable' }]
        },
        {
            Id: GROUPNUMBER_FIELD_STOP_LOSS,
            Title: 'Stop Loss',
            dropdownOptions: [{ options: stopLossOptions, Title: 'Stop Loss' }]
        },
        {
            Id: GROUPNUMBER_FIELD_STATUS,
            Title: 'Status',
            dropdownOptions: [{ options: statusOptions, Title: 'Status' }]
        },
        {
            Id: bcbs_config_attributeid_GroupNumber_Contact,
            Title: 'Contact',
            dropdownOptions: [{ options: contactOptions, Title: 'Contact' }]
        },
        {
            Id: bcbs_config_attributeid_GroupNumber_GroupLeader,
            Title: 'Group Leader',
            dropdownOptions: [{ options: contactOptions, Title: 'Group Leader' }]
        }
    ];

    // Add dynamic dropdown tables for optionset and boolean flexible fields
    Object.entries(dynamicDropdownOptions).forEach(([fieldId, options]) => {
        const attribute = discoveredFlexibleFields[fieldId];
        const title = attribute?.bcbs_name ?? 'Options';
        
        dropDownTables.push({
            Id: fieldId,
            Title: title,
            dropdownOptions: [{ options: options, Title: title }]
        });
    });

    const tables: IDatasetExtract['tables'] = [
        {
            columns: columnsArray,
            data: rows,
            name: "Import Template"
        }
    ];

    const contactsSheet = buildContactsSheet(contactsByAccountId, usedRefs, contactToEntityRef);
    tables.push({
        columns: contactsSheet.columns,
        data: contactsSheet.rows,
        name: contactsSheet.name
    });

    const cesNumbers = state.data.json?.groupStructure?.cesNumbers ?? [];
    const cesClientNumbersSheet = buildCesClientNumbersSheet(cesNumbers);
    tables.push({
        columns: cesClientNumbersSheet.columns,
        data: cesClientNumbersSheet.rows,
        name: cesClientNumbersSheet.name
    });

    const departmentsSheet = buildDepartmentsSheet(state.data.json?.groupStructure?.departments ?? []);
    tables.push({
        columns: departmentsSheet.columns,
        data: departmentsSheet.rows,
        name: departmentsSheet.name
    });

    const metadataSheet = buildMetadataSheet(state.data.snapshot, state.data.json?.groupStructure, isReport ?? false);
    tables.push({
        columns: metadataSheet.columns,
        data: metadataSheet.rows,
        name: metadataSheet.name
    });

    const dataSet: IDatasetExtract = {
        name: '',
        tables: tables,
        dropDownTables: dropDownTables,
        styles: ExcelStyles,
    };

    const result = await dataSource.GenerateEditableExcelExtract(dataSet);
    return result.file;
}

// Populate sheets

function buildContactsSheet(
    contactsByAccountId: IXrmTypes_contact[] | null,
    usedRefs: { contacts: (FAM_IEntityReferenceValue | null)[] },
    contactToEntityRef: (contact: IXrmTypes_contact) => FAM_IEntityReferenceValue
): { columns: IColumnExtract[]; rows: IRowExtract[]; name: string } {
    const contactColumns: IColumnExtract[] = [
        {
            attributeId: '',
            order: 0,
            width: 40,
            index: 1,
            fieldName: 'Contacts',
            hidden: false,
            name: 'Contacts',
            dataType: EColumnExtractDataTypes.Default
        },
        {
            attributeId: '',
            order: 1,
            width: 80,
            index: 2,
            fieldName: 'Data',
            hidden: false,
            name: 'Data',
            dataType: EColumnExtractDataTypes.Default
        }
    ];

    // Add all contacts from account that haven't been used yet
    (contactsByAccountId ?? []).forEach(eachContact => {
        const ref = contactToEntityRef(eachContact);
        const ix = usedRefs.contacts.findIndex(q => q && q.id === ref.id);
        if (ix < 0) {
            usedRefs.contacts.push(ref);
        }
    });

    // Build rows in legacy format
    const contactRows: IRowExtract[] = [];
    usedRefs.contacts.forEach((entityRef, ix) => {
        if (!entityRef) return;
        contactRows.push({
            Contacts: `${entityRef.name} - (${ix})`,
            Data: btoa(encodeURIComponent(JSON.stringify(entityRef)))
        });
    });

    return {
        columns: contactColumns,
        rows: contactRows,
        name: "Contacts"
    };
}

function buildCesClientNumbersSheet(
    cesNumbers: any[] // Use the actual type if available, e.g., CesNumber[]
): { columns: IColumnExtract[]; rows: IRowExtract[]; name: string } {
    const cesClientNumberColumns: IColumnExtract[] = [
        {
            attributeId: '',
            order: 0,
            width: 40,
            index: 1,
            fieldName: 'ID',
            hidden: false,
            name: 'ID',
            dataType: EColumnExtractDataTypes.Default
        },
        {
            attributeId: '',
            order: 1,
            width: 40,
            index: 2,
            fieldName: 'CES Client Number',
            hidden: false,
            name: 'CES Client Number',
            dataType: EColumnExtractDataTypes.Default
        },
        {
            attributeId: '',
            order: 2,
            width: 20,
            index: 3,
            fieldName: 'Status',
            hidden: false,
            name: 'Status',
            dataType: EColumnExtractDataTypes.Default
        }
    ];

    const cesClientNumberRows: IRowExtract[] = (cesNumbers ?? []).map(ces => ({
        ID: ces.id,
        'CES Client Number': ces.cesNumber,
        Status: ces.deactivated ? 'Deactivated' : 'Active'
    }));

    return {
        columns: cesClientNumberColumns,
        rows: cesClientNumberRows,
        name: "CES Client Numbers"
    };
}

function buildDepartmentsSheet(
    departments: any[] // Use the actual type if available
): { columns: IColumnExtract[]; rows: IRowExtract[]; name: string } {
    const columns: IColumnExtract[] = [
        { attributeId: '', order: 0, width: 40, index: 1, fieldName: 'ID', hidden: false, name: 'ID', dataType: EColumnExtractDataTypes.Default },
        { attributeId: '', order: 1, width: 40, index: 2, fieldName: 'CES Client Number', hidden: false, name: 'CES Client Number', dataType: EColumnExtractDataTypes.Default },
        { attributeId: '', order: 2, width: 40, index: 3, fieldName: 'Department Number', hidden: false, name: 'Department Number', dataType: EColumnExtractDataTypes.Default },
        { attributeId: '', order: 3, width: 60, index: 4, fieldName: 'Description', hidden: false, name: 'Description', dataType: EColumnExtractDataTypes.Default },
        { attributeId: '', order: 4, width: 20, index: 5, fieldName: 'Status', hidden: false, name: 'Status', dataType: EColumnExtractDataTypes.Default }
    ];
    const rows: IRowExtract[] = (departments ?? []).map(dep => ({
        ID: dep.id,
        'CES Client Number': dep.cesNumberRef?.name ?? '',
        'Department Number': dep.name,
        Description: dep.description,
        Status: dep.deactivated ? 'Deactivated' : 'Active'
    }));
    return { columns, rows, name: "Departments" };
}

function buildMetadataSheet(
    snapshot: any, // Use the actual type if available
    groupStructure: any,
    isReport: boolean
): { columns: IColumnExtract[]; rows: IRowExtract[]; name: string } {
    const columns: IColumnExtract[] = [
        { attributeId: '', order: 0, width: 40, index: 1, fieldName: 'Key', hidden: false, name: 'Key', dataType: EColumnExtractDataTypes.Default },
        { attributeId: '', order: 1, width: 60, index: 2, fieldName: 'Value', hidden: false, name: 'Value', dataType: EColumnExtractDataTypes.Default },
        { attributeId: '', order: 2, width: 60, index: 3, fieldName: 'Description', hidden: false, name: 'Description', dataType: EColumnExtractDataTypes.Default }
    ];
    const rows: IRowExtract[] = [
        { Key: 'bcbs_name', Value: snapshot.bcbs_name, Description: '' },
        { Key: 'bcbs_blobversion', Value: snapshot.bcbs_blobversion, Description: '' },
        { Key: 'checksum', Value: cyrb53(JSON.stringify(groupStructure)).toLocaleString(), Description: '' },
        // isReport marks this file as a read-only Report; import must reject files with isReport === 'true'
        { Key: 'isReport', Value: isReport ? 'true' : 'false', Description: '' }
    ];
    return { columns, rows, name: "Metadata" };
}